import { apiClient } from '@borradh-workspace/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { LeadForm, UpdateLeadFormInput } from '../types';

export const useUpdateLeadForm = (options?: {
  onSuccess?: (leadForm: LeadForm) => void;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      id,
      ...input
    }: UpdateLeadFormInput & { id: string }) => {
      return apiClient.put<LeadForm>(`lead-forms/${id}`, input);
    },
    onSuccess: (leadForm) => {
      void queryClient.invalidateQueries({ queryKey: ['lead-forms'] });
      void queryClient.invalidateQueries({
        queryKey: ['lead-forms', leadForm.id],
      });
      if (leadForm.status === 'error') {
        toast.error('Form saved, but publishing failed', {
          description: leadForm.syncError || 'Save again to retry.',
        });
        return;
      }
      toast.success(
        leadForm.status === 'synced' ? 'Lead form published' : 'Lead form saved'
      );
      options?.onSuccess?.(leadForm);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update lead form');
      options?.onError?.(error);
    },
  });

  return {
    updateLeadForm: mutation.mutate,
    updateLeadFormAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
  };
};
